// The one inbox of agent requests (technical design §4.5; the owner-agents prototype), shared by the
// Agents place (every request) and the Books (`filter` = the books' own). Each request shows which
// agent asked, why, and the exact action and input it will run, which cannot be edited. Approving
// runs it as the agent with you recorded as the approver; money also needs the owner's grant for its
// kind (Q46). Declining keeps a note the agent reads next time. A stopped agent's request cannot run,
// so its Approve is locked with the reason.

import { useEffect, useState } from 'react';
import { client } from '../api.ts';
import { MONEY_KIND_LABEL, agentName, inputRows, invoiceIdsIn, moneyKindOf } from '../books/model.ts';
import type { Approval } from '../books/types.ts';
import { Empty, Loading, Panel, useAct, type Act } from '../books/ui.tsx';
import { usePaged } from '../blueprint/paged.ts';
import { DocLink } from './DocLink.tsx';
import { SaveButton, useSaveKey } from './SaveButton.tsx';
import { useDesign } from '../blueprint/design.ts';
import './approvals.css';
import '../blueprint/inbox.css';

/** A drawn lock, beside a control that is locked and the reason why (never an emoji). */
export function Lock() {
  return (
    <svg
      className="lockico"
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

export type ApprovalStatus = 'pending' | 'approved' | 'declined';
type ApprovalPage = { approvals: Approval[]; total: number; next: string | null };

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' });

/** "agent:accounting" → "accounting". */
export const agentKeyOf = (ref: string) => ref.replace(/^agent:/, '');

/** What happened when an approved request ran (QA-261): it ran, it is still to run, or why it failed. */
export function runResult(outcome: unknown): { done: boolean; failed: string | null } {
  const o = outcome as { ok?: boolean; error?: { message?: string } } | null;
  if (!o || typeof o.ok !== 'boolean') return { done: false, failed: null };
  return o.ok ? { done: true, failed: null } : { done: true, failed: o.error?.message ?? 'it was refused' };
}

/**
 * What the approver is told once an approved request has run (QA-859): it ran, or it didn't and why,
 * and that it won't run by itself later (a refused run is recorded, never retried). Null while it
 * hasn't run yet. `who` is who it runs as, with its article ("the office copilot", "you").
 */
export function approvedText(who: string, outcome: unknown): string | null {
  const ran = runResult(outcome);
  if (!ran.done) return null;
  return ran.failed
    ? `Approved, but it did not run: ${ran.failed.replace(/\.?$/, '.')} ${NOT_AGAIN}`
    : `Approved: it ran as ${who}.`;
}

/** An approved request that was refused when it ran is not tried again (approvals.execute). */
export const NOT_AGAIN = 'It won’t run by itself later: ask the agent again, or do it yourself.';

/** How long the card waits to tell what happened when an approved request ran. */
const FOLLOW_MS = [1000, 2000, 3000, 4000];

/** "Approved by Demo Owner, Nov 3, 2026, 3:00 a.m.": what was decided, by whom, when (QA-858). */
export function decisionLine(
  a: Pick<Approval, 'status' | 'decidedByName' | 'decidedAt'>,
  when: (iso: string) => string,
): string {
  const word = a.status === 'approved' ? 'Approved' : a.status === 'declined' ? 'Declined' : 'Expired';
  const by = a.status !== 'expired' && a.decidedByName ? ` by ${a.decidedByName}` : '';
  return `${word}${by}${a.decidedAt ? `, ${when(a.decidedAt)}` : ''}`;
}

/** The run's flag for text that read like instructions (domain's UNTRUSTED_FLAG). */
export const UNTRUSTED_FLAG = 'instructions_in_untrusted_input';

/**
 * Whether the run that asked found instructions in untrusted text: the flag as data first; the words
 * that lead the request's explanation too (a run still going hasn't saved its flags yet).
 */
export const isFlagged = (a: Pick<Approval, 'explanation' | 'flags'>) =>
  !!a.flags?.includes(UNTRUSTED_FLAG) || !!a.explanation?.startsWith('Instructions found');

/**
 * After an approval, reads the request back until it has run, and says how it went (QA-859): the
 * worker runs it after the decision commits, so the decision's own answer can't say.
 */
async function followApproved(id: string, agent: string, toast: (m: string) => void, after: () => void) {
  for (const ms of FOLLOW_MS) {
    await new Promise((r) => setTimeout(r, ms));
    const got = await client
      .call<{ approvals: Approval[] }>('approvals.list', { status: 'approved', limit: 20 })
      .catch(() => null);
    const text = approvedText(agent, got?.approvals.find((x) => x.id === id)?.outcome);
    if (text) {
      toast(text);
      after();
      return;
    }
  }
}

function RequestCard({
  a,
  act,
  toast,
  after,
  stopped,
  invoiceNumbers,
}: {
  a: Approval;
  act: Act;
  toast: (m: string) => void;
  /** Re-reads the list, once an approved request has run. */
  after: () => void;
  /** Why the agent that asked is stopped, or null when it is on. */
  stopped: string | null;
  invoiceNumbers: ReadonlyMap<string, string>;
}) {
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState('');
  // Pressing again after a lost answer keeps the key, so a decision is made once (QA-058).
  const approveKey = useSaveKey(`approve:${a.id}`);
  const trustKey = useSaveKey(`trust:${a.id}`);
  const declineKey = useSaveKey(`decline:${a.id}:${note}`);
  const [doing, setDoing] = useState<'approve' | 'trust' | 'decline' | null>(null);
  // Blueprint: each request is a section of its own, its one primary the recommended way (DESIGN.md §3)
  const bp = useDesign() === 'blueprint';
  const Frame = bp ? 'section' : 'li';
  const kind = moneyKindOf(a.action);
  const decided = a.status !== 'pending';
  const ran = runResult(a.outcome);
  const locked =
    a.cannotDecide ??
    (stopped
      ? `Nothing can run as the ${agentName(a.agent).toLowerCase()} now: ${stopped.charAt(0).toLowerCase()}${stopped.slice(1).replace(/\.$/, '')}.`
      : null);
  return (
    <Frame
      className={`stone request${decided ? ' decided' : ''}${bp ? ' ib-request' : ''}`}
      {...(bp ? { 'aria-label': a.actionTitle } : {})}
    >
      <div className="line-top">
        <div>
          {/* the title in words; the action's own name is in the exact input below, and on hover (F-04) */}
          <strong title={a.action}>{a.actionTitle}</strong>
          <span className="muted small">
            {agentName(a.agent)} · asked {when(a.createdAt)}
          </span>
        </div>
        <div className="chips">{kind && <span className="chip brand">{MONEY_KIND_LABEL[kind]}</span>}</div>
      </div>
      {isFlagged(a) && <p className="chip bad">Instructions found in customer text</p>}
      {a.explanation && (
        <p className="why">
          <b>Why:</b> {a.explanation}
        </p>
      )}
      {a.input !== null ? (
        // decided, it still shows exactly what was decided (QA-859)
        <details className="exact" open>
          <summary>
            {a.status === 'approved'
              ? 'Exactly what was approved'
              : a.status === 'declined'
                ? 'Exactly what it asked to run'
                : 'Exactly what will run (the input cannot be edited)'}
          </summary>
          <p className="muted small act">Runs {a.action}</p>
          <dl className="kv input">
            {inputRows(a.input, new Map([...invoiceNumbers, ...Object.entries(a.labels ?? {})])).map((r) => (
              <div key={r.label} className="kv-pair">
                <dt>{r.label}</dt>
                {/* the words; on hover, the exact value it runs with (F-04) */}
                <dd title={r.text === r.exact ? undefined : r.exact}>{r.text}</dd>
              </div>
            ))}
          </dl>
          {invoiceIdsIn(a.input).length > 0 && (
            <p className="muted small">
              Invoices in it:{' '}
              {invoiceIdsIn(a.input).map((id, i) => (
                <span key={id}>
                  {i > 0 && ', '}
                  <DocLink doc={{ kind: 'invoice', id }}>
                    {invoiceNumbers.get(id) ?? `invoice …${id.slice(-6)}`}
                  </DocLink>
                </span>
              ))}
            </p>
          )}
        </details>
      ) : (
        <p className="muted small">The details are shown only to people who could do this themselves.</p>
      )}
      {decided ? (
        <p className={`result ${a.status === 'approved' && !ran.failed ? 'ok' : 'no'}`}>
          <b>{decisionLine(a, when)}</b>
          {a.status === 'approved' && (
            <span>
              {ran.failed
                ? ` · it did not run: ${ran.failed} ${NOT_AGAIN}`
                : ran.done
                  ? ' · it ran as the agent'
                  : ' · running as the agent'}
            </span>
          )}
          {a.note && <span> · note to the agent: “{a.note}”</span>}
        </p>
      ) : declining ? (
        <div className="fgrid inline-form">
          <label className="wide">
            <span className="flabel">Note for the agent</span>
            <input
              className="field"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Tell the agent why (it reads this next time)"
            />
          </label>
          <div className="row-actions">
            <SaveButton
              busy={act.busy && doing === 'decline'}
              busyLabel="Declining…"
              disabled={act.busy}
              onClick={() => {
                setDoing('decline');
                void act.run(
                  'approvals.decide',
                  { approvalId: a.id, decision: 'decline', ...(note ? { note } : {}) },
                  'Declined; the note is kept for the agent.',
                  { idempotencyKey: declineKey.key },
                );
              }}
            >
              Decline
            </SaveButton>
            <button className={bp ? 'ib-link' : 'btn quiet'} onClick={() => setDeclining(false)}>
              Back
            </button>
          </div>
        </div>
      ) : (
        <div className="row-actions">
          <SaveButton
            // beside "act as me for 12 hours" (the prototype's recommended one), a plain approve is a word
            {...(bp && a.canTrustConnection ? { className: 'ib-link' } : {})}
            busy={act.busy && doing === 'approve'}
            busyLabel="Approving…"
            disabled={act.busy || !!locked}
            onClick={() => {
              setDoing('approve');
              const who = a.canTrustConnection ? 'you' : `the ${agentName(a.agent).toLowerCase()}`;
              void act
                .run(
                  'approvals.decide',
                  { approvalId: a.id, decision: 'approve' },
                  // not "it runs": it may still be refused when it runs (quiet hours, say), QA-859
                  `Approved: it runs as ${who} now. You’ll see here if it can’t.`,
                  { idempotencyKey: approveKey.key },
                )
                .then((ok) => {
                  if (ok) void followApproved(a.id, who, toast, after);
                });
            }}
          >
            Approve &amp; run
          </SaveButton>
          {a.canTrustConnection && (
            <SaveButton
              busy={act.busy && doing === 'trust'}
              busyLabel="Approving…"
              disabled={act.busy || !!locked}
              onClick={() => {
                setDoing('trust');
                void act.run(
                  'approvals.decide',
                  { approvalId: a.id, decision: 'approve', trustConnection: true },
                  'Approved. For the next 12 hours, your AI tool on this connection acts as you without asking.',
                  { idempotencyKey: trustKey.key },
                );
              }}
            >
              Approve, and let it act as me for 12 hours
            </SaveButton>
          )}
          <button
            className={bp ? 'ib-link' : 'btn soft'}
            disabled={!!a.cannotDecide}
            onClick={() => setDeclining(true)}
          >
            Decline…
          </button>
          {locked ? (
            <span className="lockwhy small">
              <Lock /> {locked}
            </span>
          ) : (
            <span className="muted small">Expires {when(a.expiresAt)}</span>
          )}
        </div>
      )}
    </Frame>
  );
}

export function ApprovalsInbox({
  toast,
  filter,
  title,
  intro,
  stoppedReason = () => null,
  onChange,
}: {
  toast: (m: string) => void;
  /** Which requests this inbox shows (by action name); all when absent. */
  filter?: (action: string) => boolean;
  title: string;
  intro: string;
  /** Why an agent is stopped (by key), or null. */
  stoppedReason?: (agentKey: string) => string | null;
  /** After a decision (e.g. to refresh counts elsewhere). */
  onChange?: () => void;
}) {
  const [status, setStatus] = useState<ApprovalStatus>('pending');
  const bp = useDesign() === 'blueprint';
  // a page at a time, newest first ("Load more" reads on), counted by the server
  const list = usePaged<Approval, ApprovalPage>('approvals.list', { status }, (a) => a.approvals);
  const act = useAct(toast, () => {
    list.reload();
    onChange?.();
  });
  const shown = list.rows?.filter((a) => !filter || filter(a.action)) ?? [];
  // narrowed here by `filter`, what's shown is all that's counted: "+" while more pages are unread
  const count = filter || !list.first ? `${shown.length}${list.more ? '+' : ''}` : String(list.first.total);
  const more = list.more && (
    <button type="button" className={bp ? 'ib-link ib-more' : 'btn soft'} onClick={list.more}>
      Load more
    </button>
  );
  // The invoices the requests name, by number (QA-267); one that can't be read keeps its id.
  const [numbers, setNumbers] = useState<ReadonlyMap<string, string>>(new Map());
  const ids = [...new Set(shown.flatMap((a) => invoiceIdsIn(a.input)))].filter((id) => !numbers.has(id));
  const wanted = ids.join(',');
  useEffect(() => {
    if (!wanted) return;
    let live = true;
    void Promise.all(
      wanted.split(',').map((id) =>
        client
          .call<{ number: string }>('invoices.get', { id })
          .then((r) => [id, r.number] as const)
          .catch(() => null),
      ),
    ).then((got) => {
      if (!live) return;
      setNumbers((m) => new Map([...m, ...got.filter((g): g is readonly [string, string] => g !== null)]));
    });
    return () => {
      live = false;
    };
  }, [wanted]);
  return (
    <div className="books-page">
      <div className="page-head">
        <h2 className="page-title">{title}</h2>
        <p className="muted">{intro}</p>
      </div>
      <div className="seg" role="group" aria-label="Show">
        {(
          [
            ['pending', 'Waiting'],
            ['approved', 'Approved'],
            ['declined', 'Declined'],
          ] as const
        ).map(([s, label]) => (
          <button
            key={s}
            className={`btn quiet${status === s ? ' on' : ''}`}
            aria-pressed={status === s}
            onClick={() => setStatus(s)}
          >
            {label}
          </button>
        ))}
      </div>
      {!list.rows ? (
        <Loading error={list.error} />
      ) : bp ? (
        // Blueprint: what's shown, counted, then each request as its own section
        <div className="ib-requests">
          <h2 className="h4">
            {status === 'pending' ? 'Waiting for a person' : status === 'approved' ? 'Approved' : 'Declined'}{' '}
            <span className="count">{count}</span>
          </h2>
          {shown.length === 0 ? (
            <Empty>
              {status === 'pending'
                ? 'All caught up. New requests appear here.'
                : 'Nothing decided this way yet.'}
            </Empty>
          ) : (
            shown.map((a) => (
              <RequestCard
                key={a.id}
                a={a}
                act={act}
                toast={toast}
                after={list.reload}
                stopped={stoppedReason(agentKeyOf(a.agent))}
                invoiceNumbers={numbers}
              />
            ))
          )}
          {more}
        </div>
      ) : (
        <Panel
          title={
            status === 'pending' ? 'Waiting for a person' : status === 'approved' ? 'Approved' : 'Declined'
          }
          aside={<span className="chip">{count}</span>}
        >
          {shown.length === 0 ? (
            <Empty>
              {status === 'pending'
                ? 'All caught up. New requests appear here.'
                : 'Nothing decided this way yet.'}
            </Empty>
          ) : (
            <ul className="cards">
              {shown.map((a) => (
                <RequestCard
                  key={a.id}
                  a={a}
                  act={act}
                  toast={toast}
                  after={list.reload}
                  stopped={stoppedReason(agentKeyOf(a.agent))}
                  invoiceNumbers={numbers}
                />
              ))}
            </ul>
          )}
          {more}
        </Panel>
      )}
    </div>
  );
}
